import {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import styled from 'styled-components';
import api from '../../../services/api';
import { extractListData } from '../../../utils/Utils';

// PRD PM §5 (Task 30) — kalibrasyon el sıkışma ekranları (yönetici + çalışan).
// Gerçek `CalibrationController` rotaları (grep ile doğrulanmış — Task 13/15
// briefindeki bazı uç isimleri gerçek koddan sapıyordu, aşağıdaki liste
// controller'ın kendisiyle birebir, MyGoalsPage/TeamGoalsPage'in üstündeki
// "brief'in fiil/rota adı sapabilir" dersiyle aynı):
//   GET  v1/calibrations?periodId[eq]                (yönetici ekip kuyruğu — Task 30 gap-fill)
//   GET  v1/calibrations/{id}/manager-view            ([ModuleAuthorize("PM","Write")])
//   POST v1/calibrations/{id}/manager-approve {finalLevelId}
//   GET  v1/calibrations/my                           (çalışanın kendi kayıt indeksi — Task 30 gap-fill)
//   GET  v1/calibrations/my/{id}                       (self, yalnız manager_approved sonrası — 409 notYetVisible)
//   POST v1/calibrations/my/{id}/approve {objectionNote?}
// Kompozisyon (koordinatör kararı, T13 notu): 360 özeti + hedef sonuçları AYRI
// çağrılarla getirilir (bu dosyanın kendisi hiçbir tekil "detay" ucu icat
// etmez, yalnız var olan uçları birleştirir):
//   GET v1/feedback-responses/summary?periodId&employeeId   (yönetici — yorumlar dahil, [ModuleAuthorize("PM","Read")])
//   GET v1/feedback-responses/my/summary?periodId            (çalışan — yalnız sayısal, notYetVisible 409)
//   GET v1/period-goals/team?periodId[eq]&employeeId[eq]     (yönetici — GetTeamGoalsAsync, tek çalışanın hedef
//                                                              kanıtı sunucu tarafında filtrelenir)
//   GET v1/period-goals/my?periodId[eq]                       (çalışan — kendi hedefleri)

const fetchPeriodsByStatus = async (status) => {
  const res = await api.get('/v1/evaluation-periods', { params: { 'status[eq]': status, pageSize: 50 } });
  return extractListData(res);
};

// Yönetici ekranı yalnız kalibrasyonun ANLAMLI olduğu dönemler için (kalibrasyon
// açık/kapanış aşamasında/kapanmış — evaluation/active döneminde henüz
// EnsureRecordsAsync hiç çalışmadığı için kuyruk boş olurdu).
export const useManagedCalibrationPeriods = () => {
  const [periods, setPeriods] = useState([]);
  const [periodsFetching, setPeriodsFetching] = useState(true);

  const fetchPeriods = useCallback(async () => {
    setPeriodsFetching(true);
    try {
      const [calibration, pendingClose, closed] = await Promise.all([
        fetchPeriodsByStatus('calibration'),
        fetchPeriodsByStatus('pending_close'),
        fetchPeriodsByStatus('closed'),
      ]);
      setPeriods([...calibration, ...pendingClose, ...closed]);
    } catch {
      setPeriods([]);
    } finally {
      setPeriodsFetching(false);
    }
  }, []);

  useEffect(() => { fetchPeriods(); }, [fetchPeriods]);

  return { periods, periodsFetching };
};

// Kalibrasyon durumları (invited|employee_approved|manager_approved) — hem
// yönetici ekip kuyruğu hem de çalışan sayfası için ortak rozet paleti
// (TeamGoalsPage/config'teki STATUS_COLORS deseniyle tutarlı).
export const CALIBRATION_STATUS_COLORS = {
  invited: { color: '#D97706', bg: '#FFFBEB' },
  employee_approved: { color: '#0291EF', bg: '#EFF6FF' },
  manager_approved: { color: '#16A34A', bg: '#DCFCE7' },
};

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${(p) => p.bg};
  color: ${(p) => p.color};
`;

// ── Yönetici: ekip kuyruğu ─────────────────────────────────────────────────
export const useCalibrationTeamQueue = (periodId) => {
  const [records, setRecords] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    if (!periodId) { setRecords([]); return undefined; }
    let cancelled = false;
    setFetching(true);
    api.get('/v1/calibrations', { params: { 'periodId[eq]': periodId } })
      .then((res) => {
        if (cancelled) return;
        setRecords(res.data?.status ? (res.data.data || []) : []);
      })
      .catch(() => { if (!cancelled) setRecords([]); })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [periodId, refreshTrigger]);

  return {
    records, fetching, refresh,
  };
};

// Yönetici — kayıt detayı (manager-view) + kompoze bulgular (360 özeti +
// hedef sonuçları) + nihai seviye onayı. State'siz, yalnız çağrılabilir
// fonksiyonlar (TeamGoalsPage/config'teki useTeamGoalActions deseniyle aynı).
export const useCalibrationManagerActions = () => {
  const getManagerView = useCallback(async (recordId) => {
    try {
      const res = await api.get(`/v1/calibrations/${recordId}/manager-view`);
      return res.data?.status ? res.data.data : null;
    } catch {
      return null;
    }
  }, []);

  // 404 (form yok) dahil çeşitli beklenmedik durumlar — skipErrorToast (bu
  // panel bulunamayan bir 360 formu için sessizce boş kalmalı, hata toast'ı
  // yönetici için şaşırtıcı olurdu).
  const getFeedbackSummary = useCallback(async (periodId, employeeId) => {
    try {
      const res = await api.get('/v1/feedback-responses/summary', {
        params: { periodId, employeeId },
        skipErrorToast: true,
      });
      return res.data?.status ? res.data.data : null;
    } catch {
      return null;
    }
  }, []);

  // GET /team artık employeeId filtresini sunucu tarafında destekliyor —
  // önceden dönemdeki TÜM ekip hedefleri (pageSize:100) çekilip client-side
  // daraltılıyordu (100'ü aşan ekiplerde kanıt eksik kalıyordu).
  const getEmployeeGoals = useCallback(async (periodId, employeeId) => {
    try {
      const res = await api.get('/v1/period-goals/team', {
        params: { 'periodId[eq]': periodId, 'employeeId[eq]': employeeId, pageSize: 50 },
      });
      return extractListData(res);
    } catch {
      return [];
    }
  }, []);

  // Plan 2 (Task 8) — finalLevelId dönemin skalasına ait AKTİF bir seviyenin
  // Guid'i (CalibrationManagerViewDto.ScaleLevels'tan seçilir); eski string
  // finalLevel token'ı ARTIK yok.
  // Analitik Plan 5 — expectedVersion: okunan kayıt sürümü; başkası değiştirdiyse sunucu 409
  // versionConflict döner (mesajı yakalayıcı gösterir, çağıran kaydı yeniden çeker).
  const managerApprove = useCallback(async (recordId, finalLevelId, expectedVersion) => {
    try {
      await api.post(`/v1/calibrations/${recordId}/manager-approve`, { finalLevelId, expectedVersion });
      return true;
    } catch {
      return false;
    }
  }, []);

  // PMF-NEW-01 (Faz 2, 2026-09-11) — KALİBRASYON KURTARMA. Davete hiç yanıt
  // vermeyen tek bir çalışan tüm dönemi süresiz kilitliyordu; bu uç el sıkışma
  // SIRASINI gerekçeyle atlar. Backend yalnız enterpriseAdmin/superAdmin'e izin
  // verir (403 performance.calibration.forceForbidden) ve gerekçeyi
  // PerformanceAuditLog'a yazar — UI kapısı yalnız GÖRSEL bir daraltmadır.
  const forceApprove = useCallback(async (recordId, finalLevelId, reason, expectedVersion) => {
    try {
      await api.post(`/v1/calibrations/${recordId}/force-approve`, { finalLevelId, reason, expectedVersion });
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    getManagerView, getFeedbackSummary, getEmployeeGoals, managerApprove, forceApprove,
  };
};

// ── Çalışan: kendi kalibrasyonu ────────────────────────────────────────────
// PMF-45 (Faz 2, 2026-09-11) — PARALEL DÖNEMLERDE İKİNCİ KAYIT GÖRÜNMÜYORDU.
//
// Kusur: bu hook `list.find((r) => r.status !== 'manager_approved')` ile açık
// kayıtlardan YALNIZ BİRİNİ seçiyor, diğerini hiç göstermiyordu. Yıllık +
// çeyreklik dönemi PARALEL çalıştıran kiracıda çalışan ikinci dönemin
// kalibrasyon davetini hiç göremiyor, hiç el sıkışamıyordu → o dönem
// "manager_approved" eşiğine ulaşamıyor, oturum kapanamıyor, dönem sonsuza dek
// "calibration"da kalıyordu (DB müdahalesi dışında çıkış yok).
//
// Düzeltme: TÜM kayıtlar döndürülür; sayfa birden fazla bekleyen kayıt varsa
// seçtirir. Varsayılan seçim eski davranışla BİREBİR aynı (ilk açık kayıt,
// yoksa en yeni kapanmış) — tek kayıtlı kiracıda ekran değişmez.
export const useMyCalibration = () => {
  const [records, setRecords] = useState([]);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    api.get('/v1/calibrations/my', { skipErrorToast: true })
      .then((res) => {
        if (cancelled) return;
        setRecords(res.data?.status ? (res.data.data || []) : []);
      })
      .catch(() => { if (!cancelled) setRecords([]); })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [refreshTrigger]);

  // Seçim recordId ile TUTULUR (obje referansıyla değil): handleApprove sonrası
  // refresh() listeyi yeniden kurar ve durum invited→employee_approved'a geçer;
  // kullanıcının seçtiği dönem o round-trip'te kaybolmamalı.
  const current = useMemo(() => {
    if (records.length === 0) return null;
    const picked = records.find((r) => r.recordId === selectedRecordId);
    if (picked) return picked;
    return records.find((r) => r.status !== 'manager_approved') || records[0];
  }, [records, selectedRecordId]);

  // Sayfanın seçici göstermesi YALNIZ birden fazla AÇIK kayıt varsa anlamlı —
  // kapanmış geçmiş kayıtlar seçiciyi kalabalıklaştırmamalı.
  const pendingRecords = useMemo(
    () => records.filter((r) => r.status !== 'manager_approved'),
    [records]
  );

  const selectRecord = useCallback((recordId) => setSelectedRecordId(recordId), []);

  const approve = useCallback(async (recordId, objectionNote, expectedVersion) => {
    try {
      await api.post(`/v1/calibrations/my/${recordId}/approve`, {
        objectionNote: objectionNote || undefined,
        expectedVersion,
      });
      return true;
    } catch {
      return false;
    }
  }, []);

  // Yalnız manager_approved sonrası çağrılmalı (aksi halde 409 notYetVisible —
  // GetEmployeeViewAsync'in kendi kapısı, bkz. config yorumu üstte).
  const getMyDetail = useCallback(async (recordId) => {
    try {
      const res = await api.get(`/v1/calibrations/my/${recordId}`, { skipErrorToast: true });
      return res.data?.status ? res.data.data : null;
    } catch {
      return null;
    }
  }, []);

  // Kalibrasyon yönetici onayı öncesi 409 performance.feedback.notYetVisible
  // döner (Task 12'nin kendi kapısı) — skipErrorToast + sayfa içi bilgi metni
  // (Task 29 emsali, bkz. MyPeriodResultsPage/config).
  const getMyFeedbackSummary = useCallback(async (periodId) => {
    try {
      const res = await api.get('/v1/feedback-responses/my/summary', {
        params: { periodId },
        skipErrorToast: true,
      });
      return res.data?.status ? res.data.data : null;
    } catch {
      return null;
    }
  }, []);

  const getMyGoals = useCallback(async (periodId) => {
    try {
      const res = await api.get('/v1/period-goals/my', {
        params: { 'periodId[eq]': periodId, pageSize: 100 },
      });
      return extractListData(res);
    } catch {
      return [];
    }
  }, []);

  return {
    records,
    pendingRecords,
    current,
    selectRecord,
    fetching,
    refresh,
    approve,
    getMyDetail,
    getMyFeedbackSummary,
    getMyGoals,
  };
};
